import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/StartScreen.scss";
import {
  getAuthHeaders,
  getJwt,
  getProfile,
  setJwt,
  storeProfile,
  subscribeAuthChange,
} from "../utils/auth";
import { apiFetch } from "../utils/api";

export default function StartScreen() {
  const [asciiArt, setAsciiArt] = useState("");
  const [authError, setAuthError] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const widgetRef = useRef(null);
  const BOT_USERNAME =
    process.env.REACT_APP_TELEGRAM_BOT_USERNAME || "it_communitytest_bot";
  const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8080";

  useEffect(() => {
    const handleAuthUpdate = () => {
      const authorized = Boolean(getJwt() && getProfile());
      if (authorized) {
        navigate("/profile", { replace: true });
        return;
      }
      setLoading(false);
    };

    handleAuthUpdate();
    const unsubscribe = subscribeAuthChange(handleAuthUpdate);
    window.addEventListener("storage", handleAuthUpdate);
    return () => {
      unsubscribe();
      window.removeEventListener("storage", handleAuthUpdate);
    };
  }, [navigate]);

  const handleTelegramAuth = useCallback(
    async (user) => {
      if (!user?.id) return;

      setAuthError("");
      try {
        const response = await fetch(`${API_BASE}/auth/telegram/widget`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(user),
        });

        if (!response.ok) {
          throw new Error("Ошибка авторизации");
        }

        const payload = await response.json();
        if (!payload?.jwt) {
          throw new Error("Нет jwt");
        }

        setJwt(payload.jwt);
        if (payload?.profile) {
          storeProfile(payload.profile);
          if (payload.profile.photo_url) {
            apiFetch(`${API_BASE}/users/add_photo`, {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                ...getAuthHeaders(),
              },
              body: JSON.stringify({ photo_url: payload.profile.photo_url }),
            }).catch(() => null);
          }
        }
        navigate("/profile", { replace: true });
      } catch (e) {
        console.error(e);
        setAuthError("Не удалось авторизоваться через Telegram");
      }
    },
    [API_BASE, navigate],
  );

  useEffect(() => {
    const telegram = window.Telegram?.WebApp;
    if (!telegram) return;

    const initData = telegram.initData;
    if (!initData) return;

    const authorizeWebApp = async () => {
      try {
        setAuthError("");

        const response = await fetch(`${API_BASE}/auth/telegram/webapp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ init_data: initData }),
        });

        if (!response.ok) {
          throw new Error("Ошибка авторизации WebApp");
        }

        const payload = await response.json();

        if (!payload?.jwt) {
          throw new Error("Нет jwt");
        }

        setJwt(payload.jwt);

        if (payload?.profile) {
          storeProfile(payload.profile);
        }

        navigate("/profile", { replace: true });
      } catch (e) {
        console.error(e);
        setAuthError("Не удалось авторизоваться через Telegram WebApp");
      }
    };

    authorizeWebApp();
  }, [API_BASE, navigate]);

  useEffect(() => {
    if (loading) return;
    fetch(`${process.env.PUBLIC_URL}/ascii-art.txt`)
      .then((response) => response.text())
      .then((text) => setAsciiArt(text))
      .catch(() => setAsciiArt(""));
  }, [loading]);

  useEffect(() => {
    window.onTelegramAuth = handleTelegramAuth;
    return () => {
      delete window.onTelegramAuth;
    };
  }, [handleTelegramAuth]);

  useEffect(() => {
    if (!widgetRef.current || loading) {
      return;
    }

    widgetRef.current.innerHTML = "";
    const script = document.createElement("script");
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.async = true;
    script.setAttribute("data-telegram-login", BOT_USERNAME);
    script.setAttribute("data-size", "large");
    script.setAttribute("data-userpic", "true");
    script.setAttribute("data-request-access", "write");
    script.setAttribute("data-onauth", "onTelegramAuth(user)");
    widgetRef.current.appendChild(script);
  }, [BOT_USERNAME, loading]);

  if (!loading) {
    return (
      <div className="start-screen">
        <section className="hero">
          <div className="hero__top">
            <div className="hero__actions">
              <button className="btn btn--ghost" type="button">
                Поддержка
              </button>
              <button className="btn btn--primary" type="button">
                Соцсети
              </button>
            </div>
          </div>

          <div className="hero__content">
            <h1 className="hero__title">TaskManager</h1>
            <p className="hero__subtitle">it-сообщество</p>
            <div className="hero__auth">
              <div ref={widgetRef} className="hero__auth-widget" />
              {authError && <p className="hero__auth-error">{authError}</p>}
            </div>
          </div>
        </section>

        <aside className="ascii-panel" aria-hidden="true">
          <pre className="ascii-panel__art">{asciiArt}</pre>
        </aside>
      </div>
    );
  } else {
    return null;
  }
}
